"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {i > 0 && <span aria-hidden="true" className="inline-block size-1 rounded-lg bg-secondary/60 mx-1" />}
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-foreground bg-transparent hover:bg-transparent focus:bg-transparent font-mono text-xs uppercase tracking-[0.15em] opacity-80 hover:opacity-100 transition-opacity">
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="group flex items-baseline gap-5 text-foreground hover:text-secondary transition-colors">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-secondary opacity-70">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl sm:text-4xl font-light tracking-tight">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function NavigationPortal() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3467" id="navigation-portal" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 ${scrolled ? "bg-background/70 backdrop-blur-xl border-b border-secondary/30" : "bg-transparent border-b border-transparent"}`}>
      <div className={`mx-auto w-full max-w-7xl px-6 lg:px-10 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-[52px]" : "h-16"}`}>
        {}
        <Link to="/" aria-label="SolObservatory — Home" className="flex items-center shrink-0">
          <WvcLogo className={`w-auto transition-all duration-500 ${scrolled ? "h-5" : "h-6"}`} />
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="33" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button asChild variant="outline" className="h-9 border border-secondary text-secondary bg-transparent hover:bg-secondary/10 hover:text-secondary px-5 font-mono text-[11px] uppercase tracking-[0.2em] rounded-md">
            <Link to="/#join-the-observation">Join the Observation</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center">
          <SafeHydrate><Drawer direction="right" open={mobileOpen} onOpenChange={setMobileOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-foreground/5 hover:text-foreground">
                <Menu className="size-6" strokeWidth={1.25} />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="bg-background text-foreground border-l border-secondary/20 w-full sm:max-w-md">
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between px-6 h-16 border-b border-secondary/15">
                  <Link to="/" onClick={() => setMobileOpen(false)} aria-label="SolObservatory — Home">
                    <WvcLogo className="h-6 w-auto" />
                  </Link>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-foreground/5 hover:text-foreground">
                      <X className="size-6" strokeWidth={1.25} />
                    </Button>
                  </DrawerClose>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-12">
                  <MenuProvider menu_id="33" className="header-menu">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 py-8 border-t border-secondary/15 space-y-5">
                  <Button asChild variant="outline" className="w-full h-12 border border-secondary text-secondary bg-transparent hover:bg-secondary/10 hover:text-secondary font-mono text-[11px] uppercase tracking-[0.2em] rounded-md">
                    <Link to="/#join-the-observation" onClick={() => setMobileOpen(false)}>
                      Join the Observation
                    </Link>
                  </Button>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 text-center">
                    Observational Data. Live Streams.
                  </p>
                </div>
              </div>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </section>;
}